import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { uniqBy } from 'lodash';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { UnsavedChangesConfirmDialog } from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePageUnsavedChanges } from '@/hooks/usePageUnsavedChanges';
import { useAuthStore } from '@/stores/auth-store';
import { WizardShell } from './components/WizardShell';
import { WizardIntroSkeleton } from './components/WizardIntroSkeleton';
import { EXPLICIT_NO_MANAGER } from './components/DepartmentResolutionTable';
import { WelcomeStep } from './steps/WelcomeStep';
import { OutcomesStep } from './steps/OutcomesStep';
import { DepartmentsStep } from './steps/DepartmentsStep';
import { UsersStep } from './steps/UsersStep';
import { ManagerResolutionStep } from './steps/ManagerResolutionStep';
import {
  OrgFunctionSurfaceStep,
  type OrgFunctionSurfaceDrafts
} from './steps/OrgFunctionSurfaceStep';
import { SystemsStep } from './steps/SystemsStep';
import { ProcessesStep } from './steps/ProcessesStep';
import { OverviewStep } from './steps/OverviewStep';
import {
  clampToReachable,
  deriveResumeStep,
  nextStepId,
  previousStepId,
  wizardStepDef,
  type WizardStepId
} from './wizardSteps';
import { useInitializeUnresolvedDepartments } from '@/api/queries/useInitializeUnresolvedDepartments';
import { useAnchorDraftChangeHandler } from './useAnchorDraftChangeHandler';
import { useAnchorSeedRows } from './useAnchorSeedRows';
import { flushedSurfaceMembershipKeys, type WizardFlushBaselines } from './utils/flushBaselines';
import { effectiveAnchorRows, protectedUserIds } from './utils/protectedRows';
import { usePersonioSeeds } from './usePersonioSeeds';
import { useWizardProgress } from './useWizardProgress';
import { useWizardAutosave } from './autosave/useWizardAutosave';
import { useWizardValidationContext } from './useWizardValidationContext';
import { useWizardSubmit, type WizardDrafts } from './useWizardSubmit';
import type { ParsedDepartment, ParsedUser } from './utils/excelParsers';
import type { ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { PersonioSetupUnsuppliedPerson } from '@bliks-io/contracts';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';

const routeApi = getRouteApi('/$slug/initial-setup');

const EMPTY_SURFACE_DRAFTS: OrgFunctionSurfaceDrafts = {
  rows: [],
  memberships: [],
  removedEntityIds: [],
  membershipRemovals: []
};

export function InitialSetupWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useAuthStore().slug();
  const search = routeApi.useSearch();

  useDocumentTitle('initialSetup.title');

  const { progress, isLoading } = useWizardProgress();
  const { departmentSeeds, userSeeds, systemSeeds, processSeeds, seedsLoading } =
    useAnchorSeedRows();

  // Local Excel drafts — intentionally client-only; server-side progress is
  // the resume layer (see useWizardProgress).
  const [departments, setDepartments] = useState<ParsedDepartment[]>([]);
  const [users, setUsers] = useState<ParsedUser[]>([]);
  const [managerAssignments, setManagerAssignments] = useState<Map<string, string>>(new Map());
  const [functionDrafts, setFunctionDrafts] =
    useState<OrgFunctionSurfaceDrafts>(EMPTY_SURFACE_DRAFTS);
  const [teamDrafts, setTeamDrafts] = useState<OrgFunctionSurfaceDrafts>(EMPTY_SURFACE_DRAFTS);
  const [systems, setSystems] = useState<ParsedSystemRow[]>([]);
  const [processes, setProcesses] = useState<InitializeProcessDraft[]>([]);
  const [submitError, setSubmitError] = useState<string | undefined>();
  // Opt-in on the overview step: also send login invitations to every
  // never-invited member when the wizard is completed. Default off.
  const [inviteOnFinish, setInviteOnFinish] = useState(false);
  // Skip is never persisted (ADR 0010), so an in-session set marks the optional
  // steps the user acknowledged by skipping — the rail shows a muted check for
  // them so they read as "seen", distinct from an untouched step.
  const [skippedSteps, setSkippedSteps] = useState<Set<WizardStepId>>(new Set());
  const personioSeeds = usePersonioSeeds();
  // The last upload's, kept here so they outlive the users step.
  const [uploadUnsupplied, setUploadUnsupplied] = useState<PersonioSetupUnsuppliedPerson[]>([]);
  const unsupplied = useMemo(
    () => uniqueUnsupplied([...(personioSeeds.unsupplied ?? []), ...uploadUnsupplied]),
    [personioSeeds.unsupplied, uploadUnsupplied]
  );

  // Per-step flush baselines the autosave engine and Weiter diff against. The
  // anchors are frozen from the seeds the instant the first edit lands:
  // classifyAnchorDrafts diffs the captured draft against this snapshot rather
  // than the live seeds, so a background refetch (or a late-resolving
  // unresolved-departments/org-members query) between the first edit and
  // Weiter can no longer turn untouched rows into spurious surgical deletes
  // (C4 data-loss fix). Silent flushes move the baseline to rows-as-sent;
  // Weiter's success paths clear it.
  const [baselines, setBaselines] = useState<WizardFlushBaselines>({});

  const drafts: WizardDrafts = useMemo(
    () => ({
      departments,
      users,
      managerAssignments,
      functionDrafts,
      teamDrafts,
      systems,
      processes
    }),
    [departments, users, managerAssignments, functionDrafts, teamDrafts, systems, processes]
  );

  // Open manager resolutions lock every later step (isReachable), so this
  // derivation lands EVERY entry path — rail clicks, Überspringen, deep
  // links, refresh, browser history — on the resolve sub-step while
  // resolutions are open.
  const currentStep: WizardStepId = clampToReachable(
    search.step ?? deriveResumeStep(progress),
    progress
  );

  // Stable seeds object: the autosave engine's dirty memo is keyed on it.
  const seeds = useMemo(
    () => ({ departmentSeeds, userSeeds, systemSeeds, processSeeds }),
    [departmentSeeds, userSeeds, systemSeeds, processSeeds]
  );
  // Single instance shared by the Weiter submit AND the autosave engine — the
  // two validation passes must stay identical or autosave commits rows Weiter
  // would reject.
  const validation = useWizardValidationContext(userSeeds);

  const autosave = useWizardAutosave({
    drafts,
    baselines,
    setBaselines,
    seeds,
    progress,
    validation,
    setDepartments,
    setUsers,
    setFunctionDrafts,
    setTeamDrafts,
    setSystems,
    setProcesses
  });
  const { notifyChange, settle, setSuspended } = autosave;

  const handleDepartmentsChange = useAnchorDraftChangeHandler({
    baselineKey: 'departments',
    isDone: progress.departmentsDone,
    drafts: departments,
    baselines,
    seeds: departmentSeeds,
    setRows: setDepartments,
    setBaselines,
    notifyChange
  });

  const handleUsersChange = useAnchorDraftChangeHandler({
    baselineKey: 'users',
    isDone: progress.usersDone,
    drafts: users,
    baselines,
    seeds: userSeeds,
    setRows: setUsers,
    setBaselines,
    notifyChange
  });

  const handleFunctionDraftsChange = useCallback(
    (next: OrgFunctionSurfaceDrafts) => {
      setFunctionDrafts(next);
      notifyChange();
    },
    [notifyChange]
  );

  const handleTeamDraftsChange = useCallback(
    (next: OrgFunctionSurfaceDrafts) => {
      setTeamDrafts(next);
      notifyChange();
    },
    [notifyChange]
  );

  const handleSystemsChange = useAnchorDraftChangeHandler({
    baselineKey: 'systems',
    isDone: progress.systemsDone,
    drafts: systems,
    baselines,
    seeds: systemSeeds,
    setRows: setSystems,
    setBaselines,
    notifyChange
  });

  const handleProcessesChange = useAnchorDraftChangeHandler({
    baselineKey: 'processes',
    isDone: progress.processesDone,
    drafts: processes,
    baselines,
    seeds: processSeeds,
    setRows: setProcesses,
    setBaselines,
    notifyChange
  });

  const handleManagerAssignmentChange = useCallback(
    (deptId: string, managerId: string | undefined) => {
      setManagerAssignments((prev) => {
        const next = new Map(prev);
        // Record an explicit clear as the sentinel (not a delete) so it stays
        // distinguishable from "never touched" — otherwise the reader would
        // re-apply the email-matched manager on clear.
        if (managerId) next.set(deptId, managerId);
        else next.set(deptId, EXPLICIT_NO_MANAGER);
        return next;
      });
    },
    []
  );

  const goTo = useCallback(
    (step: WizardStepId) => {
      setSubmitError(undefined);
      navigate({
        to: '/$slug/initial-setup',
        params: { slug },
        search: { step }
      });
    },
    [navigate, slug]
  );

  const { submitCurrentStep, isSaving } = useWizardSubmit({
    currentStep,
    drafts,
    progress,
    seeds,
    baselines,
    setBaselines,
    validation,
    settleAutosave: autosave.settle,
    setDepartments,
    setUsers,
    setFunctionDrafts,
    setTeamDrafts,
    setSystems,
    setProcesses,
    setManagerAssignments,
    setSubmitError,
    inviteOnFinish,
    hasPendingChanges: autosave.isDirty,
    goTo,
    onFinished: () => navigate({ to: '/$slug', params: { slug } })
  });

  const stepDef = wizardStepDef(currentStep);
  const isStepDone = stepDef.isDone?.(progress) ?? false;

  // The engine must not start a flush while Weiter's own mutation is in
  // flight — the concurrent round would double-send the delta Weiter is
  // committing (and the post-Weiter reset would race it).
  useEffect(
    function suspendAutosaveDuringSubmit() {
      setSuspended(isSaving);
    },
    [isSaving, setSuspended]
  );

  // Weiter runs in two beats: settle the autosave, then submit from the NEXT
  // render. A settled in-flight flush has just committed baselines and
  // refetched seeds/progress — submitting from the click-time closure would
  // classify against the pre-flush world and re-create the committed rows.
  // The tick effect is deliberate (not a you-might-not-need-an-effect case):
  // an effect after commit is the only ordering React guarantees for "run
  // with the post-flush state applied".
  const [submitTick, setSubmitTick] = useState(0);
  // Spans the whole Weiter click — settle, the tick, and the submit — since
  // isSaving only covers the step mutation itself.
  const [isAdvancing, setIsAdvancing] = useState(false);
  const isBusy = isSaving || isAdvancing;
  const submitCurrentStepRef = useRef(submitCurrentStep);
  const clickedStepRef = useRef<WizardStepId>(currentStep);
  const currentStepRef = useRef(currentStep);
  useEffect(function mirrorSubmitCurrentStep() {
    submitCurrentStepRef.current = submitCurrentStep;
    currentStepRef.current = currentStep;
  });
  useEffect(
    function runSubmitAfterSettle() {
      if (submitTick === 0) return;
      // Settling an in-flight flush can repopulate the unresolved managers
      // and flip the derived step into the resolve detour between click and
      // tick — submitting the NEW step's handler under the old click would
      // act on a step the user never confirmed. The re-render already shows
      // the detour; drop the click instead.
      const submitted =
        clickedStepRef.current === currentStepRef.current
          ? submitCurrentStepRef.current()
          : Promise.resolve();
      void submitted.finally(() => setIsAdvancing(false));
    },
    [submitTick]
  );

  // Every step — including an untouched seeded anchor table — flows through the
  // submit (which settles the autosave again itself, as a guard for any other
  // caller); submitAnchorStep short-circuits an empty seeded draft into pure
  // navigation itself, so the step-kind knowledge stays out of the page.
  const handleNext = useCallback(() => {
    if (isBusy) return;
    setIsAdvancing(true);
    clickedStepRef.current = currentStep;
    void settle().then(() => setSubmitTick((tick) => tick + 1));
  }, [currentStep, isBusy, settle]);

  const handleSkip = useCallback(() => {
    // Überspringen writes nothing (skip is derived, never stored) — only the
    // in-session acknowledgement is recorded so the rail can flag it as seen.
    setSkippedSteps((prev) => new Set(prev).add(currentStep));
    const next = nextStepId(currentStep);
    if (next) goTo(next);
  }, [currentStep, goTo]);

  const handleBack = useCallback(() => {
    const previous = previousStepId(currentStep, progress);
    if (previous) goTo(previous);
  }, [currentStep, goTo, progress]);

  // A draft that autosave already committed is not "unsaved" — only an actual
  // delta against the flush baselines (or a pending/in-flight flush) plus the
  // never-autosaved manager assignments arm the leave-page blocker. An
  // assignment only counts while its department is still unresolved: a
  // department resolved through another path (departments edit, users
  // re-import) would otherwise keep a stale sentinel arming the dialog for
  // the rest of the session.
  const { data: unresolvedDepartments } = useInitializeUnresolvedDepartments();
  const hasOpenAssignments = useMemo(() => {
    const openIds = new Set((unresolvedDepartments ?? []).map((dept) => dept._id));
    return [...managerAssignments.keys()].some((id) => openIds.has(id));
  }, [managerAssignments, unresolvedDepartments]);
  const hasUnsavedChanges = hasOpenAssignments || autosave.isDirty;
  const { showConfirmation, handleStay, handleDiscard } = usePageUnsavedChanges({
    hasUnsavedChanges
  });

  // seedsLoading included: an edit before the seed queries resolve would
  // freeze an empty seed list as the flush baseline (see useAnchorSeedRows).
  if (isLoading || seedsLoading) {
    return <WizardIntroSkeleton />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep />;
      case 'outcomes':
        return <OutcomesStep />;
      case 'departments':
        return (
          <DepartmentsStep
            departments={departments}
            onDepartmentsChange={handleDepartmentsChange}
            submitError={submitError}
            isDone={isStepDone}
            flushBaseline={baselines.departments}
            personioSeed={personioSeeds.departments}
          />
        );
      case 'users':
        return (
          <UsersStep
            users={users}
            onUsersChange={handleUsersChange}
            submitError={submitError}
            isDone={isStepDone}
            flushBaseline={baselines.users}
            personioSeed={personioSeeds.users}
            unsupplied={unsupplied}
            onUploadUnsupplied={setUploadUnsupplied}
          />
        );
      case 'resolve':
        return (
          <ManagerResolutionStep
            managerAssignments={managerAssignments}
            onAssignmentChange={handleManagerAssignmentChange}
            isSubmitting={isBusy}
          />
        );
      case 'functions':
        return (
          <OrgFunctionSurfaceStep
            // key resets local UI state (active tab) between the two steps
            // that render this same component type at the same position
            key="role"
            kind="role"
            drafts={functionDrafts}
            onDraftsChange={handleFunctionDraftsChange}
            submitError={submitError}
            isDone={isStepDone}
            flushedMembershipKeys={flushedSurfaceMembershipKeys(baselines.functions)}
          />
        );
      case 'teams':
        return (
          <OrgFunctionSurfaceStep
            key="team"
            kind="team"
            drafts={teamDrafts}
            onDraftsChange={handleTeamDraftsChange}
            submitError={submitError}
            isDone={isStepDone}
            flushedMembershipKeys={flushedSurfaceMembershipKeys(baselines.teams)}
            personioSeed={personioSeeds.teams}
          />
        );
      case 'systems':
        return (
          <SystemsStep
            systems={systems}
            onSystemsChange={handleSystemsChange}
            submitError={submitError}
            isDone={isStepDone}
            flushBaseline={baselines.systems}
          />
        );
      case 'processes':
        return (
          <ProcessesStep
            processes={processes}
            onProcessesChange={handleProcessesChange}
            submitError={submitError}
            isDone={isStepDone}
            flushBaseline={baselines.processes}
          />
        );
      case 'overview':
        return (
          <OverviewStep
            onNavigateToStep={goTo}
            inviteOnFinish={inviteOnFinish}
            onInviteOnFinishChange={setInviteOnFinish}
          />
        );
      default:
        return currentStep satisfies never;
    }
  };

  const nextLabel = t(stepDef.nextLabelKey ?? 'initialSetup.footer.next');

  // Only the two non-skippable anchor steps block Weiter while empty; a done
  // step always allows Weiter (an empty seeded draft short-circuits into pure
  // navigation in submitAnchorStep). The users gate counts the SAME effective
  // rows the table displays and the submit acts on — a step whose only rows
  // are protected members (the solo pre-placed admin) must stay submittable,
  // or the placement PATCH that stamps the step is unreachable.
  const usersEffectiveRows = effectiveAnchorRows({
    draftRows: users,
    seedRows: userSeeds,
    protectedIds: protectedUserIds(validation.orgMembers ?? [], progress.usersDone),
    isDone: progress.usersDone,
    baseline: baselines.users
  });
  const isNextDisabled =
    (currentStep === 'departments' && departments.length === 0 && !progress.departmentsDone) ||
    (currentStep === 'users' && usersEffectiveRows.length === 0 && !progress.usersDone);

  return (
    <>
      <WizardShell
        currentStep={currentStep}
        progress={progress}
        skippedSteps={skippedSteps}
        onStepClick={goTo}
        onBack={currentStep === 'welcome' ? undefined : handleBack}
        onNext={handleNext}
        onSkip={stepDef.skippable ? handleSkip : undefined}
        nextLabel={nextLabel}
        nextIcon={stepDef.nextIcon}
        isIntro={stepDef.isIntro}
        isSaving={isBusy}
        nextDisabled={isNextDisabled}
        autosaveStatus={autosave.status}
      >
        {renderStep()}
      </WizardShell>

      <UnsavedChangesConfirmDialog
        open={showConfirmation}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
    </>
  );
}

/** One entry per person: by email, else by name for those without one. */
function uniqueUnsupplied(people: PersonioSetupUnsuppliedPerson[]) {
  return uniqBy(people, ({ email, firstName, lastName }) =>
    email ? normalizedKey(email) : `name:${normalizedKey(`${firstName} ${lastName}`)}`
  );
}
